import React, { Fragment, useEffect, useState } from 'react';

import { CreationStatus, GetCreationOutput, PostingType } from '@/api/creation';
import { ReactComponent as ChevronDownIcon } from '@/assets/icons/bx-chevron-down.svg';
import { ReactComponent as ChevronRightIcon } from '@/assets/icons/bx-chevron-right.svg';
import { ReactComponent as PencilImage } from '@/assets/icons/bx-pencil.svg';
import { Button } from '@/components/Elements';
import { deleteCreationImage, postCreationApprove } from '@/hooks/Creation';
import { getImageUrlIfRelative } from '@/utils/image';

import { ReactComponent as EditIcon } from './assets/bx-edit-alt.svg';
import { ReactComponent as MenuLeftIcon } from './assets/bx-menu-alt-left.svg';
import CommentIcon from './assets/CommentIcon.png';
import { ReactComponent as InformationImage } from './assets/Information carousel_Two Color 1.svg';
import { ImageModal } from './ImageModal';
import { DeleteModal } from './modals/DeleteModal';

interface ImageCreateViewProps {
  postDetail: GetCreationOutput;
  updatePost: () => Promise<GetCreationOutput>;
  handleComment: () => void;
}

export type CreationImageDraftContent = {
  media:
    | {
        type: 'path';
        mediaImage: string;
        mediaId: number;
      }
    | {
        type: 'file';
        mediaImage: File;
      };
  textImage: string;
};

export type CreationImageDraft = {
  contents: CreationImageDraftContent[];
  description: string;
  movieURL: string;
  movieDescription: string;
};

export const ImageCreateView = ({ postDetail, updatePost, handleComment }: ImageCreateViewProps) => {
  const [creationImageDraft, setCreationImageDraft] = useState<CreationImageDraft>({
    contents:
      postDetail.draft?.images?.map((image) => {
        return {
          media: {
            ...image,
            type: 'path',
          },
          textImage: image.textImage,
        };
      }) ?? [],
    description: postDetail?.draft?.description || '',
    movieURL: postDetail?.draft?.movieUrl || '',
    movieDescription: postDetail?.draft?.movieDescription || '',
  });
  const [mediaImageIndex, setImageMediaIndex] = useState<number>(0);
  // const [imageCreationErrorMessage, setImageCreationErrorMessage] = useState<string>('');
  const changeMediaImageIndex = (index: number, length: number) => {
    if (length > index + 1) {
      const newIndex = index + 1;
      setImageMediaIndex(newIndex);
    }
    if (length === index + 1) {
      setImageMediaIndex(0);
    }
  };
  const statusToStepNum = (status: CreationStatus): number => {
    switch (status) {
      case CreationStatus.Draft:
        return 1;
      case CreationStatus.DraftReview:
        return 2;
      case CreationStatus.Contents:
        return 3;
      case CreationStatus.ContentsReview:
        return 4;
      case CreationStatus.Completed:
        return 5;
      default:
        return 0;
    }
  };

  const [isImageModalOpen, setIsImageModalOpen] = useState<boolean>(false);

  const [imageDeleteModal, setImageDeleteModal] = useState<boolean>(false);
  const handleImageDeleteModal = () => {
    setImageDeleteModal(true);
  };

  const [imageApproved, setImageApproved] = useState<boolean>(false);

  const handleImageApproved = () => {
    if (
      postDetail.type === PostingType.XPost ||
      creationImageDraft.contents.length > 0 ||
      creationImageDraft.movieURL.length > 0
    ) {
      postCreationApprove(postDetail.id, postDetail.draft?.contentId ?? '').then(() => {
        updatePost();
        setImageApproved(true);
      });
    }
  };

  const handleImageDelete = () => {
    deleteCreationImage(postDetail.id, postDetail.draft?.contentId ?? '').then(() => {
      updatePost();
    });
    setImageDeleteModal(false);
  };

  const CreationImage = (content: CreationImageDraftContent) => {
    switch (content.media.type) {
      case 'path':
        return content.media.mediaImage.includes('.mp4') ? (
          <video controls src={getImageUrlIfRelative(content.media.mediaImage)}>
            <track kind="captions" />
          </video>
        ) : (
          <img src={getImageUrlIfRelative(content.media.mediaImage)} alt="" />
        );
      case 'file':
        return content.media.mediaImage.type == 'video/mp4' ? (
          <video controls src={URL.createObjectURL(content.media.mediaImage)}>
            <track kind="captions" />
          </video>
        ) : (
          <img src={URL.createObjectURL(content.media.mediaImage)} alt="" />
        );
    }
  };

  useEffect(() => {
    setCreationImageDraft({
      contents:
        postDetail.draft?.images?.map((image) => {
          return {
            media: {
              ...image,
              type: 'path',
            },
            textImage: image.textImage,
          };
        }) ?? [],
      description: postDetail?.draft?.description || '',
      movieURL: postDetail?.draft?.movieUrl || '',
      movieDescription: postDetail?.draft?.movieDescription || '',
    });
  }, [postDetail.draft]);

  return (
    <>
      {statusToStepNum(postDetail.status) > 1 ||
      creationImageDraft.contents.length ||
      creationImageDraft.movieURL.length ? (
        <div className="box-content max-w-[1133px] px-[24px]">
          <div className="my-[25px] w-full rounded-lg bg-white p-6 shadow-[0_1px_2px_rgba(0,0,0,0.06),0_1px_3px_rgba(0,0,0,0.1)]">
            <div className="w-full">
              <div className="mb-[24px] flex w-full items-center justify-between">
                <div className="flex items-center">
                  <span className="text-lg font-semibold leading-7 text-gray-500">イメージ案</span>
                </div>
                <div className="flex items-center gap-[30px]">
                  {/* ToDo 下記のpostStatusになっている部分を全てselectedPost.statusに戻す */}
                  {statusToStepNum(postDetail.status) > 2 || imageApproved ? (
                    <div className="flex items-center justify-between bg-green-100 px-[8px] py-[2px]">
                      <span className="text-sm font-medium leading-5 text-green-800">承認済</span>
                      <button>
                        <ChevronDownIcon fill="#10B981" width={16} height={16} />
                      </button>
                    </div>
                  ) : (
                    <Button onClick={handleImageApproved} className="text-sm font-medium leading-5">
                      承認する
                    </Button>
                  )}
                  <button
                    onClick={() => setIsImageModalOpen(true)}
                    disabled={postDetail.status !== CreationStatus.DraftReview}
                  >
                    <EditIcon width={24} height={24} fill="#6B7280" />
                  </button>
                  <button onClick={handleComment}>
                    {/* <CommentIcon width={24} height={24} /> */}
                    <img src={CommentIcon} alt="" />
                  </button>
                </div>
              </div>
              <div className="w-full">
                {postDetail.type !== PostingType.XPost && (
                  <>
                    <div className="mb-[8px] flex items-center">
                      <span className="text-sm font-semibold leading-5 text-gray-700">メディア</span>
                    </div>
                    {postDetail.type === PostingType.Youtube ? (
                      <div className="mb-[24px] flex flex-col gap-y-4 pl-3">
                        <div>
                          <div className="text-sm font-semibold text-gray-800">YouTubeの動画URL</div>
                          <p className="text-xs font-normal text-gray-800">{creationImageDraft.movieURL}</p>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-800">動画の説明</div>
                          <p className="text-xs font-normal text-gray-800">{creationImageDraft.movieDescription}</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="mb-[8px] flex items-center">
                          <div className="mr-[5px] flex items-center">
                            <span className="text-sm font-semibold leading-5 text-gray-700">
                              {mediaImageIndex + 1} / {creationImageDraft.contents.length}枚目
                            </span>
                          </div>
                          <button
                            onClick={() => changeMediaImageIndex(mediaImageIndex, creationImageDraft.contents.length)}
                          >
                            <ChevronRightIcon width={28} height={28} fill="#374151" />
                          </button>
                        </div>
                        <div className="mb-[24px] flex py-[10px]">
                          <div
                            className="flex w-[290px] shrink-0 grow-0 items-center justify-center"
                            style={{ aspectRatio: '290 / 253.6' }}
                          >
                            {creationImageDraft.contents.length > mediaImageIndex &&
                              CreationImage(creationImageDraft.contents[mediaImageIndex])}
                          </div>
                          <div className="ml-20 flex-1 py-[10px]">
                            <div className="mb-[20px] flex items-center gap-2">
                              <MenuLeftIcon width={20} height={20} fill="#000000" />
                              <div className="flex items-center">
                                <span className="text-xs font-normal leading-4 text-gray-800">画像 / 動画の説明</span>
                              </div>
                            </div>
                            <div className="text-xs font-normal leading-4 text-gray-800">
                              {creationImageDraft.contents[mediaImageIndex]?.textImage || 'No description'}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                    <div className="mb-[24px] h-px w-full border-b border-[#DDDDDD]"></div>
                  </>
                )}
                <div className="w-full">
                  <div className="mb-[8px] h-[20px] w-full px-[16px]">
                    <span className="h-[20px] w-[56px] text-sm font-semibold leading-5 text-gray-800">テキスト</span>
                  </div>
                  <div className="w-full p-[16px]">{postDetail && postDetail.draft?.description}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="box-content max-w-[1133px] px-[24px] py-[25px]">
          <div className="flex h-[525px] w-full items-center justify-center rounded bg-[#FFFFFF] p-[24px] shadow-[0_1px_2px_rgba(0,0,0,0.06),0_1px_3px_rgba(0,0,0,0.1)]">
            <div className="h-[478px] w-full max-w-[1085px]">
              <div className="mb-[24px] w-full">
                <p className="text-lg font-semibold leading-7 text-gray-500">イメージ案</p>
              </div>
              <div className="flex w-full items-center justify-center py-[48px]">
                <div className="pb-[4px]">
                  <div className="mb-[24px]">
                    <InformationImage width={252} height={136} />
                    <div className="mt-[8px] flex justify-center">
                      <p className="w-[252px] text-center text-[14px] font-normal leading-5 text-[#6B7280]">
                        イメージ案がまだ制作されていません。
                      </p>
                    </div>
                  </div>
                  <Button
                    startIcon={<PencilImage fill="#FFFFFF" className="h-[20px] w-[20px]" />}
                    onClick={() => setIsImageModalOpen(true)}
                  >
                    イメージ案を制作する
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="relative">
        {DeleteModal({
          type: 'image',
          open: imageDeleteModal,
          handleDelete: handleImageDelete,
          handleCancel: () => setImageDeleteModal(false),
        })}
      </div>
      <ImageModal
        isOpen={isImageModalOpen}
        creationId={postDetail.id}
        contentId={postDetail.draft?.contentId || '123456789ABCDEF'}
        postingStatus={postDetail.status}
        imageDetail={{
          ...creationImageDraft,
          contents: creationImageDraft.contents,
          postingType: postDetail.type,
        }}
        closeModal={() => setIsImageModalOpen(false)}
        deleteModal={handleImageDeleteModal}
        updatePost={updatePost}
      />
    </>
  );
};
